/* ==========================================================================
   WARRIOR wall writing: ROAM drawn on, left to drip, rotating through a bank.
   DESIGN.md {components.roam-wall-writing}; markup in snippets/custom.wall-writing.liquid.

   - Large desktops only: does nothing until (min-width: data-min-width) matches,
     and only then fetches the pre-traced glyphs (assets/custom.roam-glyphs.json).
   - Each line is tilted by a random angle between data-angle-min and -max,
     rising left to right. Drips are computed after the tilt so they always run
     straight down, the way paint does.
   - Pauses while off screen or in a background tab. Reduced motion: one static
     line, no drips, no cycling.
   - Host mode (data-layout="host", the home hero): the wall sits over a real
     heading and writes that heading's own text, on its own line breaks, at its
     own size and alignment. data-spray="false" drops the overspray specks,
     data-speed scales the pen, data-start="dom" starts before window load.
   ========================================================================== */
(function () {
  'use strict';
  // init(root) runs one wall. Exposed as window.WarriorWallInit so a wall made
  // later (the Konami easter egg in custom.js) can be started on any page,
  // alongside a collection masthead's own wall.
  function init(root) {
  if (!root || root.dataset.ready) return;
  root.dataset.ready = '1';

  var NS = 'http://www.w3.org/2000/svg';
  var svg = root.querySelector('.custom-wall__svg');
  var phrases = [];
  try { phrases = JSON.parse(root.querySelector('[data-custom-wall-phrases]').textContent) || []; } catch (e) {}
  phrases = phrases.filter(function (p) { return p && p.trim(); });
  if (!phrases.length || !svg) return;

  var minW = parseInt(root.dataset.minWidth, 10); // 0 is valid (the Konami wall shows at any width)
  var mq = window.matchMedia('(min-width: ' + (isNaN(minW) ? 1280 : minW) + 'px)');
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // 0 is a valid angle (the hero writes level), so test for NaN, not falsy.
  var aMin = parseFloat(root.dataset.angleMin); if (isNaN(aMin)) aMin = 4;
  var aMax = parseFloat(root.dataset.angleMax); if (isNaN(aMax)) aMax = 10;
  aMax = Math.max(aMin, aMax);
  var spray = root.dataset.spray !== 'false';
  var speed = Math.max(0.25, parseFloat(root.dataset.speed) || 1);
  // Host mode: the text node of the heading this wall covers.
  var host = root.dataset.layout === 'host' ? root.parentNode.querySelector('[data-custom-wall-host]') : null;
  var drips = root.dataset.drips !== 'false';
  var cycle = root.dataset.cycle === 'true'; // off: one line per page load, left in place once written
  var outlineOnly = root.dataset.style === 'outline';
  // Solid letters can be thinned below ROAM's own weight: an erode filter takes
  // `thin` em off every edge.
  var thin = Math.max(0, parseFloat(root.dataset.thin) || 0) / 1000;

  // DESIGN.md motion tokens: draw-glyph / draw-stagger / draw-hold / draw-wipe.
  // Solid letters are painted left to right, one character after the next, like a hand writing.
  var T = { glyph: 420, stagger: 85, hold: 2400, wipe: 600 };
  // Hand-writing pace for solid letters: pen speed in font units per ms, plus a short pen lift.
  var PEN = { speed: 4.5 * speed, lift: 19 / speed, gap: 27 / speed };
  var OUTLINE_EM = 0.02; // visible outline; drawn at 2x because the fill covers the inner half

  var G = null, U = 500, token = 0, align = 'center', last = null;
  function rand(a, b) { return a + Math.random() * (b - a); }
  function el(tag, attrs, parent) {
    var e = document.createElementNS(NS, tag);
    for (var k in attrs) e.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(e);
    return e;
  }
  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function shuffle(a) { a = a.slice(); for (var i = a.length - 1; i > 0; i--) { var j = Math.floor(Math.random() * (i + 1)), t = a[i]; a[i] = a[j]; a[j] = t; } return a; }
  function cssVar(name) { return getComputedStyle(root).getPropertyValue(name).trim(); }
  // On screen = the element's own box overlaps the viewport, measured on every poll.
  // (An IntersectionObserver flag proved unreliable for a wall unhidden right after
  // creation: the Konami overlay sat blank waiting for an update that never came.)
  function onScreen() {
    var b = root.getBoundingClientRect();
    return b.width > 0 && b.height > 0 && b.bottom > 0 && b.top < (window.innerHeight || 0);
  }
  function whenVisible() {
    return new Promise(function (r) { (function wait() { if (onScreen() && !document.hidden) r(); else setTimeout(wait, 400); })(); });
  }

  // --- layout -------------------------------------------------------------
  // Host mode: read the heading's own line breaks from the browser's layout, so
  // the written lines break exactly where the real text does at this width.
  function hostLines() {
    var t = host && host.firstChild;
    if (!t || t.nodeType !== 3) return null;
    var str = t.data, r = document.createRange(), out = [], cur = '', top = null, off = 0;
    str.split(/(\s+)/).forEach(function (w) {
      if (!w) return;
      if (!w.trim()) { off += w.length; if (cur) cur += ' '; return; }
      r.setStart(t, off); r.setEnd(t, off + w.length);
      var rc = r.getClientRects()[0], y = rc ? Math.round(rc.top) : 0;
      if (top === null) top = y;
      if (Math.abs(y - top) > 2 && cur.trim()) { out.push(cur.trim()); cur = ''; top = y; }
      cur += w; off += w.length;
    });
    if (cur.trim()) out.push(cur.trim());
    return out.length ? out : null;
  }
  function hostAlign() {
    var a = host ? getComputedStyle(host).textAlign : 'center';
    var rtl = host && getComputedStyle(host).direction === 'rtl';
    if (a === 'center') return 'center';
    if (a === 'right' || (a === 'end' && !rtl) || (a === 'start' && rtl)) return 'end';
    return 'start';
  }
  // Host mode: one font unit = the heading's own font-size / upm, so the written
  // line is the same size as the text it replaces, anchored on the same edge.
  // The heading's font-size, line-height and first baseline (px from the top of
  // its line box: half-leading + the font's ascent), in the heading's own CSS px.
  function hostMetrics() {
    var cs = getComputedStyle(host), fs = parseFloat(cs.fontSize) || 16;
    var lhp = parseFloat(cs.lineHeight); if (isNaN(lhp)) lhp = fs * 1.2;
    var c = document.createElement('canvas').getContext('2d');
    c.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + cs.fontSize + ' ' + cs.fontFamily;
    var m = c.measureText('Hg'), fa = m.fontBoundingBoxAscent || fs * 0.8, fd = m.fontBoundingBoxDescent || fs * 0.2;
    return { fs: fs, lh: lhp, base: (lhp - (fa + fd)) / 2 + fa };
  }
  function sizeToHost() {
    var hm = hostMetrics(), k = hm.fs / U, vb = svg.viewBox.baseVal;
    svg.style.width = (vb.width * k) + 'px';
    svg.style.height = (vb.height * k) + 'px';
    var edge = align === 'start' ? '0%' : align === 'end' ? '100%' : '50%';
    svg.style.left = 'calc(' + edge + ' + ' + (vb.x * k) + 'px)';
    // Glyph y = 0 is the first baseline, so put it on the heading's first baseline.
    svg.style.top = (hm.base + vb.y * k) + 'px';
  }
  function place(extra) { fit(extra); if (host) sizeToHost(); }
  function finish(s) { s.glyphs.forEach(function (o) { if (o.ink) { o.path.setAttribute('fill-opacity', 1); o.ink.style.display = 'none'; } }); outline(s, false); }
  // Host outline: when the host text has a -webkit-text-stroke (the PDP flavour
  // sticker's ink edge), the finished letters get the same stroke drawn behind
  // the fill (paint-order), so the written line IS the final look and nothing
  // swaps or moves when the writing ends. Doubled width: half sits under the fill.
  function outline(s, fade) {
    if (!host) return;
    var cs = getComputedStyle(host), w = parseFloat(cs.webkitTextStrokeWidth) || 0;
    if (!w) return;
    var sw = (w * 2) / (hostMetrics().fs / U), col = cs.webkitTextStrokeColor || cs.color;
    s.glyphs.forEach(function (o) {
      var pth = o.path;
      pth.setAttribute('stroke', col);
      pth.setAttribute('stroke-width', sw);
      pth.setAttribute('stroke-linejoin', 'round');
      pth.style.paintOrder = 'stroke fill';
      if (fade && !reduced) {
        pth.style.strokeOpacity = '0';
        requestAnimationFrame(function () { pth.style.transition = 'stroke-opacity .2s ease'; pth.style.strokeOpacity = '1'; });
      }
    });
  }
  function width(text) {
    var w = 0;
    for (var i = 0; i < text.length; i++) {
      var g = G.g[text[i]]; if (g) w += g[0];
      if (i < text.length - 1) w += G.k[text[i] + text[i + 1]] || 0;
    }
    return w;
  }
  function wrap(text, maxW) {
    var words = text.split(' '), out = [], cur = '';
    words.forEach(function (w) { var t = cur ? cur + ' ' + w : w; if (cur && width(t) > maxW) { out.push(cur); cur = w; } else cur = t; });
    if (cur) out.push(cur);
    return out;
  }

  // Builds one phrase: letters in a tilted group, an untilted layer for drips.
  // Solid style: each letter is hand-written by a broad pen that follows its
  // centre-line strokes in writing order. No outline, no fade, no glow.
  // Lightening is a true erode (feMorphology), so overlapping contours never show.
  function build(text, colours) {
    svg.innerHTML = '';
    var sw = OUTLINE_EM * U;
    var uidp = 'w' + Math.random().toString(36).slice(2, 7);
    var defs = el('defs', {}, svg), back = el('g', {}, svg), tilt = el('g', {}, svg), front = el('g', {}, svg);
    var spray = el('g', {}, tilt);
    align = host ? hostAlign() : 'center';
    var hl = host ? hostLines() : null;
    var lines = hl || wrap(text, U * 4.6), glyphs = [], lh = host ? hostMetrics().lh / (hostMetrics().fs / U) : U * 0.92;
    var nozzle = null;
    if (!outlineOnly) {
      nozzle = el('filter', { id: uidp + 'n', x: '-20%', y: '-20%', width: '140%', height: '140%' }, defs);
      el('feTurbulence', { type: 'fractalNoise', baseFrequency: '0.09', numOctaves: '2', seed: String(Math.floor(rand(1, 999))), result: 't' }, nozzle);
      el('feDisplacementMap', { in: 'SourceGraphic', in2: 't', scale: U * 0.035, xChannelSelector: 'R', yChannelSelector: 'G' }, nozzle);
      if (thin > 0) {
        var er = el('filter', { id: uidp + 'e', x: '-5%', y: '-5%', width: '110%', height: '110%' }, defs);
        el('feMorphology', { operator: 'erode', radius: thin * U }, er);
        tilt.setAttribute('filter', 'url(#' + uidp + 'e)');
      }
    }
    lines.forEach(function (line, li) {
      var x = align === 'start' ? 0 : align === 'end' ? -width(line) : -width(line) / 2, y = li * lh;
      for (var i = 0; i < line.length; i++) {
        var g = G.g[line[i]];
        if (g && g[1]) {
          var tr = 'translate(' + x + ' ' + y + ')', o = { x: x, y: y, line: li, last: li === lines.length - 1 };
          if (outlineOnly) {
            o.path = el('path', { d: g[1], transform: tr, fill: 'none', stroke: colours.outline, 'stroke-width': sw, 'stroke-linejoin': 'round', 'stroke-linecap': 'round' }, tilt);
          } else {
            // The pen: the letter's own centre-line strokes (pre-computed in
            // custom.roam-glyphs.json), painted in writing order with a broad round
            // nib and clipped to the letter's shape, so it is hand-written. Painted
            // strokes, not a mask: browsers do not repaint masks while they animate.
            var cid = uidp + 'c' + glyphs.length;
            var cp = el('clipPath', { id: cid }, defs);
            el('path', { d: g[1], transform: tr }, cp);
            o.path = el('path', { d: g[1], transform: tr, fill: colours.fill, 'fill-opacity': 0 }, tilt);
            o.ink = el('g', { 'clip-path': 'url(#' + cid + ')' }, tilt);
            o.pens = (g[2] || []).map(function (st) {
              return el('path', { d: st[1], transform: tr, fill: 'none', stroke: colours.fill, 'stroke-width': st[0], 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, o.ink);
            });
            if (!o.pens.length) { o.pens = [el('path', { d: g[1], transform: tr, fill: 'none', stroke: colours.fill, 'stroke-width': U * 0.2 }, o.ink)]; }
            o.nozzle = true;
          }
          glyphs.push(o);
        }
        if (g) x += g[0];
        if (i < line.length - 1) x += G.k[line[i] + line[i + 1]] || 0;
      }
    });
    // Writing schedule: one character after another, strokes in order.
    var clock = 0;
    glyphs.forEach(function (o, i) {
      if (o.pens) {
        o.start = clock;
        o.pens.forEach(function (p) { p.len = p.getTotalLength(); p.at = clock; p.dur = Math.max(60, p.len / PEN.speed); clock += p.dur + PEN.lift; });
        o.end = clock; clock += PEN.gap;
      } else { o.start = i * T.stagger; o.end = o.start + T.glyph; }
    });
    var bb = tilt.getBBox(), cx = bb.x + bb.width / 2, cy = bb.y + bb.height / 2;
    var angle = -rand(aMin, aMax);
    tilt.setAttribute('transform', 'rotate(' + angle + ' ' + cx + ' ' + cy + ')');
    var rad = angle * Math.PI / 180, cos = Math.cos(rad), sin = Math.sin(rad);
    var toPage = function (px, py) { var dx = px - cx, dy = py - cy; return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos }; };
    return { defs: defs, back: back, tilt: tilt, front: front, spray: spray, glyphs: glyphs, toPage: toPage, sw: outlineOnly ? sw : U * 0.03, fill: colours.fill };
  }
  function fit(extraBottom) {
    var b = svg.getBBox(), pad = U * 0.12;
    svg.setAttribute('viewBox', (b.x - pad) + ' ' + (b.y - pad) + ' ' + (b.width + pad * 2) + ' ' + (b.height + pad * 2 + (extraBottom || 0)));
  }

  // --- draw ---------------------------------------------------------------
  function dash(path, len) { path.style.strokeDasharray = len + ' ' + len; }
  function draw(s) {
    var anims = [];
    s.glyphs.forEach(function (o, i) {
      o.len = o.path.getTotalLength();
      if (reduced) { if (o.ink) { o.path.setAttribute('fill-opacity', 1); o.ink.style.display = 'none'; } return; }
      var d = i * T.stagger;
      if (o.pens) {
        d = o.start;
        o.pens.forEach(function (p) {
          // Gap = length + 2 nibs, start offset = length + 1 nib. With a plain
          // len/len dash the round cap of the hidden dash peeks out at the
          // stroke's start: a dot that shows before the letter is written.
          var nib = parseFloat(p.getAttribute('stroke-width')) || 0;
          p.nib = nib;
          p.style.strokeDasharray = p.len + ' ' + (p.len + nib * 2);
          anims.push(p.animate([{ strokeDashoffset: p.len + nib }, { strokeDashoffset: 0 }], { duration: p.dur, delay: p.at, easing: 'cubic-bezier(.35,.1,.45,1)', fill: 'both' }));
        });
      } else {
        dash(o.path, o.len);
        anims.push(o.path.animate([{ strokeDashoffset: o.len }, { strokeDashoffset: 0 }], { duration: T.glyph, delay: d, easing: 'cubic-bezier(.45,0,.4,1)', fill: 'both' }));
      }
      if (o.nozzle && spray) {
        // Overspray: a few fine specks land around the letter as the can passes.
        var box = o.path.getBBox();
        for (var k = 0; k < 5; k++) {
          var dot = el('circle', { cx: o.x + rand(box.x - U * 0.04, box.x + box.width + U * 0.04), cy: o.y + rand(box.y - U * 0.04, box.y + box.height + U * 0.06),
            r: U * rand(0.004, 0.011), fill: s.fill, opacity: 0 }, s.spray);
          anims.push(dot.animate([{ opacity: 0 }, { opacity: rand(0.25, 0.6) }], { duration: 80, delay: o.start + rand(0, o.end - o.start), fill: 'both' }));
        }
      }
    });
    return Promise.all(anims.map(function (a) { return a.finished; })).catch(function () {}).then(function () {
      // Swap the pen strokes for the solid letter so every letter ends crisp and complete.
      s.glyphs.forEach(function (o) { if (o.ink) { o.path.setAttribute('fill-opacity', 1); o.ink.style.display = 'none'; } });
    });
  }

  // Paint runs: origins sampled from the bottom 15% of each glyph's own contour
  // AFTER the tilt, so a run never starts in thin air and always falls vertically.
  function smooth(pts) {
    var d = 'M' + pts[0][0] + ' ' + pts[0][1];
    for (var i = 0; i < pts.length - 1; i++) {
      var p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
      d += ' C' + (p1[0] + (p2[0] - p0[0]) / 6) + ' ' + (p1[1] + (p2[1] - p0[1]) / 6) + ' ' + (p2[0] - (p3[0] - p1[0]) / 6) + ' ' + (p2[1] - (p3[1] - p1[1]) / 6) + ' ' + p2[0] + ' ' + p2[1];
    }
    return d + 'Z';
  }
  function runShape(x, y0, L, w) {
    var n = 7, widths = [1.55, 1.0], xs = [0], drift = 0, left = [], right = [];
    for (var k = 2; k < n; k++) widths.push(rand(0.42, 0.82) * (k === n - 1 ? 0.9 : 1));
    for (k = 1; k < n; k++) { drift += rand(-w * 0.18, w * 0.18); xs.push(drift); }
    for (k = 0; k < n; k++) {
      var y = y0 + L * Math.pow(k / (n - 1), 1.15);
      left.push([x + xs[k] - w * widths[k] * 0.5, y]);
      right.unshift([x + xs[k] + w * widths[k] * 0.5, y]);
    }
    return { d: smooth(left.concat(right)), bx: x + xs[n - 1], by: y0 + L };
  }
  function addDrips(s, paint) {
    var id = 'w' + Math.random().toString(36).slice(2, 8), anims = [], taken = [], deepest = 0;
    var f = el('filter', { id: id, x: '-30%', y: '-10%', width: '160%', height: '130%' }, s.defs);
    el('feTurbulence', { type: 'fractalNoise', baseFrequency: '0.02', numOctaves: '2', seed: String(Math.floor(rand(1, 999))), result: 'n' }, f);
    el('feDisplacementMap', { in: 'SourceGraphic', in2: 'n', scale: outlineOnly ? s.sw * 0.9 : U * 0.05, xChannelSelector: 'R', yChannelSelector: 'G' }, f);
    // In front of the letters so the trim stroke never cuts a gap between letter and run.
    var layer = el('g', { filter: 'url(#' + id + ')' }, s.front);
    s.glyphs.forEach(function (o, i) {
      var len = o.len || o.path.getTotalLength(), pts = [], lo = -Infinity, hi = Infinity;
      for (var k = 0; k < 90; k++) {
        var p = o.path.getPointAtLength(len * k / 90), q = s.toPage(p.x + o.x, p.y + o.y);
        pts.push(q); lo = Math.max(lo, q.y); hi = Math.min(hi, q.y);
      }
      var base = pts.filter(function (q) { return q.y >= lo - (lo - hi) * 0.15; });
      var runs = Math.random() < 0.62 ? (Math.random() < 0.25 ? 2 : 1) : 0;
      for (var r = 0; r < runs && base.length; r++) {
        var q = base[Math.floor(Math.random() * base.length)];
        if (taken.some(function (x) { return Math.abs(x - q.x) < U * 0.09; })) continue;
        taken.push(q.x);
        // Runs from a hollow outline are thin like the line itself; from a filled letter, heavier.
        var w = (outlineOnly ? s.sw * rand(1.3, 2.4) : U * rand(0.035, 0.07)) * (r ? 0.65 : 1), L = U * (Math.random() < 0.3 ? rand(0.08, 0.22) : rand(0.3, 1.25)) * (r ? 0.55 : 1);
        // Runs from an upper line stay short, so paint never streaks through the letters below.
        if (!o.last) L = Math.min(L, U * rand(0.06, 0.16));
        var y0 = q.y - w * 0.9, shape = runShape(q.x, y0, L, w), bead = w * rand(0.62, 0.9);
        deepest = Math.max(deepest, y0 + L + bead * 1.4);
        var cid = id + 'c' + i + r, cp = el('clipPath', { id: cid }, s.defs);
        var rect = el('rect', { x: q.x - w * 3, y: y0 - w, width: w * 6, height: L + w * 3 }, cp);
        var run = el('g', { 'clip-path': 'url(#' + cid + ')' }, layer);
        el('ellipse', { cx: q.x, cy: y0 + w * 0.35, rx: w * rand(1.6, 2.4), ry: w * 0.8, fill: paint }, run);
        el('path', { d: shape.d, fill: paint }, run);
        el('ellipse', { cx: shape.bx, cy: shape.by + bead * 0.2, rx: bead, ry: bead * 1.15, fill: paint }, run);
        rect.style.transformBox = 'fill-box';
        rect.style.transformOrigin = '50% 0';
        var stall = rand(0.35, 0.6);
        anims.push(rect.animate([
          { transform: 'scaleY(0)', easing: 'cubic-bezier(.3,.6,.4,1)' },
          { transform: 'scaleY(' + stall + ')', offset: 0.3, easing: 'linear' },
          { transform: 'scaleY(' + (stall + 0.05) + ')', offset: 0.55, easing: 'cubic-bezier(.4,0,.6,1)' },
          { transform: 'scaleY(1)' }
        ], { duration: rand(1800, 3400), delay: (o.end != null ? o.end : i * T.stagger + T.glyph) + rand(80, 500), fill: 'both' }));
      }
    });
    return { layer: layer, gloss: s.front, anims: anims, deepest: deepest };
  }

  // Wipe: paint comes off left to right, the same direction it went on.
  function wipe(s, d) {
    var out = [];
    s.glyphs.forEach(function (o, k) {
      if (o.pens) {
        o.path.setAttribute('fill-opacity', 0); o.ink.style.display = '';
        o.pens.forEach(function (p) {
          out.push(p.animate([{ strokeDashoffset: 0 }, { strokeDashoffset: -(p.len + (p.nib || 0)) }], { duration: T.wipe * 0.5, delay: k * 30, easing: 'cubic-bezier(.5,0,.6,1)', fill: 'both' }));
        });
      } else {
        dash(o.path, o.len);
        out.push(o.path.animate([{ strokeDashoffset: 0 }, { strokeDashoffset: -o.len }], { duration: T.wipe, delay: k * 25, easing: 'cubic-bezier(.5,0,.6,1)', fill: 'both' }));
      }
    });
    out.push(s.spray.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 300, fill: 'both' }));
    if (d) { out.push(d.layer.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 360, fill: 'both' })); out.push(d.gloss.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, fill: 'both' })); }
    return Promise.all(out.map(function (a) { return a.finished; })).catch(function () {});
  }

  // --- loop ---------------------------------------------------------------
  async function run() {
    var my = ++token, order = shuffle(phrases), i = 0;
    var colours = { fill: cssVar('--wall-fill') || 'currentColor', outline: cssVar('--wall-outline') || 'currentColor', ground: cssVar('--wall-ground') || '#fff' };
    // Host mode paints in the host's own resolved colour (the PDP flavour name is
    // coloured per flavour on the host element itself).
    if (host) { var hc = getComputedStyle(host).color; colours.fill = hc; colours.outline = hc; }
    while (my === token) {
      await whenVisible(); if (my !== token) return;
      if (host) root.parentNode.classList.remove('is-written');
      // Host mode writes the host's current text (it changes with the flavour).
      var s = build(host ? (host.textContent || '').trim() : order[i], colours);
      last = { text: order[i], colours: colours };
      if (reduced) { finish(s); place(); root.dispatchEvent(new CustomEvent('wall:done')); return; }
      var d = drips ? addDrips(s, outlineOnly ? colours.outline : colours.fill) : null;
      place();
      var drawn = draw(s);
      await drawn;
      outline(s, true);
      var settle = d && d.anims.length ? Promise.all(d.anims.map(function (a) { return a.finished; })).catch(function () {}) : Promise.resolve();
      if (!cycle) { // stays on the wall; drips keep running to completion
        settle.then(function () { root.dispatchEvent(new CustomEvent('wall:done')); });
        return;
      }
      await Promise.all([settle, sleep(T.hold)]);
      await sleep(T.hold * 0.4);
      if (my !== token) return;
      await wipe(s, d);
      await sleep(250);
      i = (i + 1) % order.length;
      if (i === 0) order = shuffle(phrases);
    }
  }

  function load() {
    if (G) return Promise.resolve();
    return fetch(root.dataset.glyphs).then(function (r) { return r.json(); }).then(function (j) { G = j; U = j.upm || 500; });
  }
  function check() {
    if (mq.matches) {
      var ready = load();
      if (host && document.fonts && document.fonts.load) {
        var cs = getComputedStyle(host);
        ready = Promise.all([ready, document.fonts.load(cs.fontSize + ' ' + cs.fontFamily).catch(function () {})]);
      }
      ready.then(function () { root.hidden = false; run(); }).catch(function () { root.hidden = true; });
    } else {
      token++; root.hidden = true; svg.innerHTML = '';
    }
  }

  if (mq.addEventListener) mq.addEventListener('change', check); else if (mq.addListener) mq.addListener(check);
  // Host mode: when the heading reflows (resize, rotate), redraw the finished
  // line statically on the new breaks. Mid-write resizes are left to finish.
  if (host) {
    var rz = 0;
    window.addEventListener('resize', function () {
      clearTimeout(rz);
      rz = setTimeout(function () {
        if (!last || root.hidden || !root.dataset.done) return;
        finish(build(last.text, last.colours)); place();
      }, 200);
    });
    // .is-written on the wrapper lets CSS hand over to the real text when done
    // (the PDP flavour sticker keeps its ink outline, which the pen cannot draw).
    root.addEventListener('wall:done', function () { root.dataset.done = '1'; root.parentNode.classList.add('is-written'); });
    // Rewrite when the host's text changes (PDP flavour name on a variant change).
    // The same text written again (custom.js repaints a few times) is ignored.
    var lastText = (host.textContent || '').trim();
    new MutationObserver(function () {
      var now = (host.textContent || '').trim();
      if (!now || now === lastText) return;
      lastText = now;
      if (!G || root.hidden) return;
      delete root.dataset.done;
      run();
    }).observe(host, { childList: true, characterData: true, subtree: true });
  }
  if (document.readyState === 'complete' || root.dataset.start === 'dom') check(); else window.addEventListener('load', check, { once: true });
  }

  window.WarriorWallInit = window.WarriorWallInit || init;
  document.querySelectorAll('[data-custom-wall]').forEach(function (r) { window.WarriorWallInit(r); });
})();
